import { useEffect, useMemo, useState } from 'react';
import useReengagement from './hooks/useReengagement';

interface ReengageModalProps {
    activationId: number;
    onClose: () => void;
}

/**
 * Classic (v1) reengagement tool: send every unique share destination in the
 * event a fresh link to their gallery, with a custom message. The v2 app has
 * its own skin over the same useReengagement hook.
 */
export default function ReengageModal({
    activationId,
    onClose,
}: ReengageModalProps) {
    const {
        preview,
        isLoadingPreview,
        loadPreview,
        activeSend,
        isStarting,
        start,
        error,
    } = useReengagement(activationId);

    const [channels, setChannels] = useState<'both' | 'text' | 'email'>(
        'both',
    );
    const [textBody, setTextBody] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const wantsText = channels !== 'email';
    const wantsEmail = channels !== 'text';

    const recipientCount = useMemo(() => {
        if (!preview) return 0;
        if (channels === 'text') return preview.textCount;
        if (channels === 'email') return preview.emailCount;
        return preview.total;
    }, [preview, channels]);

    const canSend =
        recipientCount > 0 &&
        (!wantsText || textBody.trim().length > 0) &&
        (!wantsEmail || emailBody.trim().length > 0) &&
        !isStarting;

    const onSend = async () => {
        if (!canSend) return;

        if (
            !window.confirm(
                `Send to ${recipientCount} recipient${
                    recipientCount === 1 ? '' : 's'
                }? This cannot be undone.`,
            )
        ) {
            return;
        }

        await start({
            channels,
            textBody: wantsText ? textBody : undefined,
            emailSubject: wantsEmail ? emailSubject : undefined,
            emailBody: wantsEmail ? emailBody : undefined,
        });
    };

    const progress = activeSend
        ? activeSend.sentCount +
          activeSend.failedCount +
          activeSend.skippedCount
        : 0;
    const progressPercent = activeSend?.totalCount
        ? Math.round((progress / activeSend.totalCount) * 100)
        : 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/40"
                onClick={activeSend ? undefined : onClose}
            />
            <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-1 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Reengage guests
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        ✕
                    </button>
                </div>

                {!activeSend && (
                    <>
                        <p className="mb-4 text-sm text-gray-500">
                            Every unique destination this event shared to gets
                            a fresh link to their own gallery, with your
                            message.
                        </p>

                        {isLoadingPreview && (
                            <p className="text-sm text-gray-500">
                                Loading recipients…
                            </p>
                        )}

                        {preview && (
                            <div className="mb-4 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                <b>{preview.total}</b> unique recipient
                                {preview.total === 1 ? '' : 's'} —{' '}
                                {preview.textCount} phone number
                                {preview.textCount === 1 ? '' : 's'},{' '}
                                {preview.emailCount} email
                                {preview.emailCount === 1 ? '' : 's'}
                                {preview.optedOutCount > 0 && (
                                    <>
                                        {' '}
                                        · {preview.optedOutCount} opted out
                                        (will be skipped)
                                    </>
                                )}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                                Send via
                            </label>
                            <div className="flex gap-2">
                                {(
                                    [
                                        ['both', 'Text + Email'],
                                        ['text', 'Text only'],
                                        ['email', 'Email only'],
                                    ] as const
                                ).map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setChannels(value)}
                                        className={`rounded-full px-3 py-1 text-sm font-semibold ${
                                            channels === value
                                                ? 'bg-black text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {wantsText && (
                            <div className="mb-4">
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                    Text message
                                </label>
                                <textarea
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none"
                                    rows={3}
                                    value={textBody}
                                    placeholder="Relive the party! Your photos are waiting:"
                                    onChange={(e) =>
                                        setTextBody(e.target.value)
                                    }
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Each recipient's gallery link is appended
                                    automatically. ~
                                    {Math.ceil(
                                        (textBody.length + 30) / 160,
                                    )}{' '}
                                    SMS segment
                                    {Math.ceil((textBody.length + 30) / 160) ===
                                    1
                                        ? ''
                                        : 's'}
                                    .
                                </p>
                            </div>
                        )}

                        {wantsEmail && (
                            <>
                                <div className="mb-4">
                                    <label className="mb-1 block text-sm font-medium text-gray-700">
                                        Email subject{' '}
                                        <span className="font-normal text-gray-400">
                                            (optional — falls back to the
                                            gallery's subject)
                                        </span>
                                    </label>
                                    <input
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none"
                                        value={emailSubject}
                                        placeholder="Your photos are waiting!"
                                        onChange={(e) =>
                                            setEmailSubject(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="mb-1 block text-sm font-medium text-gray-700">
                                        Email body
                                    </label>
                                    <textarea
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:border-black focus:outline-none"
                                        rows={6}
                                        value={emailBody}
                                        placeholder="Plain text or HTML. Use *|SHAREURL|* to place the gallery link."
                                        onChange={(e) =>
                                            setEmailBody(e.target.value)
                                        }
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Plain text or an HTML template. Put{' '}
                                        <code>*|SHAREURL|*</code> where the
                                        gallery link belongs — without it, the
                                        link is appended at the end.
                                    </p>
                                </div>
                            </>
                        )}

                        {error && (
                            <p className="mb-3 text-sm font-semibold text-red-600">
                                {error}
                            </p>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={onSend}
                                disabled={!canSend}
                                className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm ${
                                    canSend
                                        ? 'bg-black hover:bg-gray-800'
                                        : 'cursor-not-allowed bg-gray-300'
                                }`}
                            >
                                {isStarting
                                    ? 'Starting…'
                                    : `Send to ${recipientCount} recipient${
                                          recipientCount === 1 ? '' : 's'
                                      }`}
                            </button>
                        </div>
                    </>
                )}

                {activeSend && (
                    <div className="mt-2">
                        <p className="mb-2 text-sm text-gray-700">
                            {activeSend.status === 'complete' &&
                                'Done — all messages processed.'}
                            {activeSend.status === 'failed' &&
                                'Finished with some failures.'}
                            {(activeSend.status === 'pending' ||
                                activeSend.status === 'running') &&
                                'Sending… you can close this window, the send continues on the server.'}
                        </p>
                        <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                            <div
                                className="h-full rounded-full bg-black transition-all"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <p className="text-sm text-gray-500">
                            {activeSend.sentCount} sent
                            {activeSend.skippedCount > 0 &&
                                ` · ${activeSend.skippedCount} skipped (opted out)`}
                            {activeSend.failedCount > 0 && (
                                <span className="font-semibold text-red-600">
                                    {' '}
                                    · {activeSend.failedCount} failed
                                </span>
                            )}{' '}
                            · {activeSend.totalCount} total
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-md bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                            >
                                {activeSend.status === 'complete' ||
                                activeSend.status === 'failed'
                                    ? 'Close'
                                    : 'Close (keeps sending)'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
